import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { BRAND } from '../lib/constants';
import { ConsentRevokeWidget } from '../components/ui/ConsentRevokeWidget';
import styles from './LegalPage.module.css';

export const CookiePolicyPage = () => {
  const { i18n } = useTranslation();
  const isIt = i18n.language === 'it';

  useDocumentMetadata(
    isIt ? 'Cookie Policy — NexTrainE' : 'Cookie Policy — NexTrainE',
    isIt ? 'Informativa sui cookie di NexTrainE S.r.l.' : 'NexTrainE S.r.l. cookie policy.'
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Link to="/" className={styles.backLink}>
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {isIt ? 'Torna alla Home' : 'Back to Home'}
        </Link>

        <p className={styles.lastUpdate}>
          {isIt ? 'Ultimo aggiornamento: 2 luglio 2026' : 'Last updated: July 2, 2026'}
        </p>
        <h1 className={styles.title}>Cookie Policy</h1>

        <div className={styles.content}>
          {isIt ? <CookieIT /> : <CookieEN />}
        </div>

        <ConsentRevokeWidget />
      </div>
    </div>
  );
};

/* ─────────────────── ITALIAN ─────────────────── */
const CookieIT = () => (
  <>
    <h2>1. Cosa sono i Cookie</h2>
    <p>
      I cookie sono piccoli file di testo che i siti web possono memorizzare nel
      browser dell'utente per ricordare informazioni relative alla visita. Esistono
      diverse tipologie: cookie tecnici (necessari al funzionamento del sito),
      cookie analitici (per statistiche) e cookie di profilazione (per pubblicità
      personalizzata).
    </p>

    <hr className={styles.divider} />

    <h2>2. Cookie utilizzati da questo sito</h2>
    <p>
      Il sito web <strong>{BRAND.name}</strong> utilizza esclusivamente{' '}
      <strong>cookie analitici di terze parti</strong> tramite Google Analytics 4
      (Google LLC) per raccogliere statistiche anonime sull'utilizzo del sito.
      Non vengono utilizzati cookie di profilazione.
    </p>

    <hr className={styles.divider} />

    <h2>3. Tecnologie di memorizzazione locale</h2>
    <p>
      Al posto dei cookie, il sito utilizza la funzionalità{' '}
      <strong>localStorage</strong> del browser per memorizzare esclusivamente la
      preferenza linguistica dell'utente (italiano o inglese). Questa informazione:
    </p>
    <ul>
      <li>Resta memorizzata solo nel browser dell'utente</li>
      <li>Non viene trasmessa a nessun server</li>
      <li>Non è un cookie secondo la definizione tecnica e normativa</li>
      <li>È eliminabile in qualsiasi momento cancellando i dati del sito dal browser</li>
    </ul>

    <table className={styles.cookieTable}>
      <thead>
        <tr>
          <th>Nome</th>
          <th>Tipo</th>
          <th>Finalità</th>
          <th>Durata</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>_ga</code></td>
          <td>Cookie analitico (terza parte)</td>
          <td>Distingue gli utenti per Google Analytics</td>
          <td>2 anni</td>
        </tr>
        <tr>
          <td><code>_ga_&lt;ID&gt;</code></td>
          <td>Cookie analitico (terza parte)</td>
          <td>Mantiene lo stato della sessione per Google Analytics</td>
          <td>2 anni</td>
        </tr>
        <tr>
          <td><code>i18nextLng</code></td>
          <td>localStorage</td>
          <td>Memorizza la lingua scelta (it/en)</td>
          <td>Persistente (fino a cancellazione manuale)</td>
        </tr>
      </tbody>
    </table>

    <hr className={styles.divider} />

    <h2>4. Servizi di terze parti</h2>

    <h3>4.1 Google Analytics 4</h3>
    <p>
      Il sito utilizza <strong>Google Analytics 4</strong> (Google LLC) per
      raccogliere statistiche di utilizzo. I cookie <code>_ga</code> e{' '}
      <code>_ga_&lt;ID&gt;</code> vengono installati nel browser dell'utente. I
      dati raccolti sono anonimi e aggregati.
    </p>
    <p>
      L'utente può disattivare Google Analytics installando il componente aggiuntivo
      per il browser:{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
        tools.google.com/dlpage/gaoptout
      </a>.
    </p>

    <p>
      Il sito carica inoltre risorse esterne che <strong>non settano cookie</strong>{' '}
      nel browser dell'utente, ma che comportano una connessione ai server dei
      rispettivi fornitori:
    </p>

    <h3>4.2 Google Fonts</h3>
    <p>
      I font tipografici vengono scaricati dai server di Google LLC. Google dichiara
      che Google Fonts non utilizza cookie e non traccia gli utenti. L'indirizzo IP
      dell'utente viene trasmesso a Google durante il download dei font.
    </p>

    <h3>4.3 Unsplash</h3>
    <p>
      Alcune immagini illustrative vengono caricate dai server di Unsplash, Inc.
      L'indirizzo IP dell'utente viene trasmesso a Unsplash durante il download.
    </p>

    <h3>4.4 Amazon CloudFront</h3>
    <p>
      Il sito è distribuito tramite Amazon CloudFront (AWS). Questa CDN non setta
      cookie sul dominio <strong>nextraine.com</strong>.
    </p>

    <hr className={styles.divider} />

    <h2>5. Come gestire i dati memorizzati</h2>
    <p>
      Per eliminare i cookie di Google Analytics e la preferenza linguistica, l'utente
      può:
    </p>
    <ul>
      <li>Aprire le impostazioni del browser</li>
      <li>Navigare alla sezione "Cookie" o "Dati dei siti"</li>
      <li>Cercare <strong>nextraine.com</strong> e cancellare i dati associati</li>
    </ul>
    <p>
      In alternativa, è possibile utilizzare la funzione "Cancella dati di
      navigazione" del proprio browser selezionando "Cookie e dati dei siti" e
      "Dati dei siti web".
    </p>
    <p>
      Per disattivare specificamente Google Analytics senza cancellare i cookie,
      installare il{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
        componente aggiuntivo di opt-out
      </a>.
    </p>

    <hr className={styles.divider} />

    <h2>6. Riferimenti normativi</h2>
    <p>
      La presente cookie policy è redatta in conformità con:
    </p>
    <ul>
      <li>Regolamento (UE) 2016/679 (GDPR)</li>
      <li>Direttiva 2002/58/CE (Direttiva ePrivacy) e relativo recepimento italiano
        (D.Lgs. 196/2003, come modificato dal D.Lgs. 101/2018)</li>
      <li>Linee guida del Garante per la protezione dei dati personali sui cookie e
        altri strumenti di tracciamento (10 giugno 2021)</li>
    </ul>

    <hr className={styles.divider} />

    <h2>7. Contatti</h2>
    <p>
      Per qualsiasi domanda relativa alla presente cookie policy, contattare:{' '}
      <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
    </p>
  </>
);

/* ─────────────────── ENGLISH ─────────────────── */
const CookieEN = () => (
  <>
    <h2>1. What Are Cookies</h2>
    <p>
      Cookies are small text files that websites may store in a user's browser to
      remember information about their visit. There are different types: technical
      cookies (necessary for website operation), analytics cookies (for statistics),
      and profiling cookies (for personalized advertising).
    </p>

    <hr className={styles.divider} />

    <h2>2. Cookies Used by This Site</h2>
    <p>
      The <strong>{BRAND.name}</strong> website uses only{' '}
      <strong>third-party analytics cookies</strong> via Google Analytics 4
      (Google LLC) to collect anonymous statistics about website usage.
      No profiling cookies are used.
    </p>

    <hr className={styles.divider} />

    <h2>3. Local Storage Technologies</h2>
    <p>
      Instead of cookies, the site uses the browser's <strong>localStorage</strong>
      {' '}feature to store only the user's language preference (Italian or English).
      This information:
    </p>
    <ul>
      <li>Is stored only in the user's browser</li>
      <li>Is not transmitted to any server</li>
      <li>Is not a cookie under technical and regulatory definitions</li>
      <li>Can be deleted at any time by clearing the site's data from the browser</li>
    </ul>

    <table className={styles.cookieTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Purpose</th>
          <th>Duration</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>_ga</code></td>
          <td>Analytics cookie (third-party)</td>
          <td>Distinguishes users for Google Analytics</td>
          <td>2 years</td>
        </tr>
        <tr>
          <td><code>_ga_&lt;ID&gt;</code></td>
          <td>Analytics cookie (third-party)</td>
          <td>Maintains session state for Google Analytics</td>
          <td>2 years</td>
        </tr>
        <tr>
          <td><code>i18nextLng</code></td>
          <td>localStorage</td>
          <td>Stores the selected language (it/en)</td>
          <td>Persistent (until manually cleared)</td>
        </tr>
      </tbody>
    </table>

    <hr className={styles.divider} />

    <h2>4. Third-Party Services</h2>

    <h3>4.1 Google Analytics 4</h3>
    <p>
      The site uses <strong>Google Analytics 4</strong> (Google LLC) to collect
      usage statistics. The cookies <code>_ga</code> and{' '}
      <code>_ga_&lt;ID&gt;</code> are set in the user's browser. The data
      collected is anonymous and aggregated.
    </p>
    <p>
      Users can opt out of Google Analytics by installing the browser add-on:{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
        tools.google.com/dlpage/gaoptout
      </a>.
    </p>

    <p>
      The site also loads external resources that <strong>do not set cookies</strong>{' '}
      in the user's browser, but which involve a connection to the respective
      providers' servers:
    </p>

    <h3>4.2 Google Fonts</h3>
    <p>
      Typefaces are downloaded from Google LLC servers. Google states that Google
      Fonts does not use cookies or track users. The user's IP address is
      transmitted to Google when downloading fonts.
    </p>

    <h3>4.3 Unsplash</h3>
    <p>
      Some illustrative images are loaded from Unsplash, Inc. servers. The user's
      IP address is transmitted to Unsplash during download.
    </p>

    <h3>4.4 Amazon CloudFront</h3>
    <p>
      The site is distributed via Amazon CloudFront (AWS). This CDN does not set
      cookies on the <strong>nextraine.com</strong> domain.
    </p>

    <hr className={styles.divider} />

    <h2>5. How to Manage Stored Data</h2>
    <p>
      To delete Google Analytics cookies and the language preference, users can:
    </p>
    <ul>
      <li>Open browser settings</li>
      <li>Navigate to "Cookies" or "Site Data" section</li>
      <li>Search for <strong>nextraine.com</strong> and delete its associated data</li>
    </ul>
    <p>
      Alternatively, you can use the "Clear browsing data" function in your browser
      and select "Cookies and site data" and "Site data".
    </p>
    <p>
      To specifically disable Google Analytics without deleting cookies, install the{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
        opt-out browser add-on
      </a>.
    </p>

    <hr className={styles.divider} />

    <h2>6. Legal References</h2>
    <p>
      This cookie policy is drafted in compliance with:
    </p>
    <ul>
      <li>Regulation (EU) 2016/679 (GDPR)</li>
      <li>Directive 2002/58/EC (ePrivacy Directive) and its Italian transposition
        (Legislative Decree 196/2003, as amended by Legislative Decree 101/2018)</li>
      <li>Italian Data Protection Authority guidelines on cookies and other tracking
        tools (June 10, 2021)</li>
    </ul>

    <hr className={styles.divider} />

    <h2>7. Contact</h2>
    <p>
      For any questions regarding this cookie policy, please contact:{' '}
      <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
    </p>
  </>
);
